import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import { toast } from "sonner";
import { 
  Globe, Calendar, Phone, User, FileText, Clock, CheckCircle, XCircle, 
  Trash2, Eye, RefreshCw, Upload, Plus, Save, Image, MessageSquare, 
  HelpCircle, BarChart3, MapPin, Edit, ChevronDown, ChevronUp, X, Camera,
  Tag, Instagram, UserCheck, Bot
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Lazy-loaded sub-pages for embedded tabs
import OffersReviewsPage from "./OffersReviewsPage";
import InstagramManagePage from "./InstagramManagePage";
import LeadsPage from "./LeadsPage";
import AIDashboardPage from "./AIDashboardPage";

// Convert relative image URLs to full URLs  
const imgUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  // Convert old disk URLs to new MongoDB-backed URLs
  if (url.startsWith("/api/uploads/website/")) {
    const fname = url.replace("/api/uploads/website/", "");
    return `${API_BASE}/api/website/image/${fname}`;
  }
  return `${API_BASE}${url}`;
};

// ==================== TAB DEFINITIONS ====================
const TABS = [
  { id: "bookings", label: "الحجوزات", icon: Calendar },
  { id: "hero", label: "الصفحة الرئيسية", icon: Globe },
  { id: "services", label: "الخدمات", icon: FileText },
  { id: "gallery", label: "قبل وبعد", icon: Image },
  { id: "photos", label: "معرض الصور", icon: Camera },
  { id: "testimonials", label: "آراء العملاء", icon: MessageSquare },
  { id: "faq", label: "الأسئلة الشائعة", icon: HelpCircle },
  { id: "stats", label: "العدادات", icon: BarChart3 },
  { id: "contact", label: "التواصل", icon: MapPin },
  { id: "offers", label: "العروض والتقييمات", icon: Tag },
  { id: "instagram", label: "إدارة انستغرام", icon: Instagram },
  { id: "leads", label: "العملاء المحتملين", icon: UserCheck },
  { id: "ai-dashboard", label: "لوحة الذكاء الاصطناعي", icon: Bot },
];

// ==================== IMAGE UPLOAD COMPONENT ====================
const ImageUpload = ({ value, onChange, label, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/api/website/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        onChange(data.url);
        toast.success("تم رفع الصورة");
      } else {
        toast.error("فشل رفع الصورة");
      }
    } catch (err) {
      toast.error("خطأ في رفع الصورة");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative w-24 h-24 rounded-lg overflow-hidden border bg-gray-50">
            <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange("")} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div 
            onClick={() => fileRef.current?.click()} 
            className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1E90FF] transition-colors"
          >
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-xs text-gray-400 mt-1">{uploading ? "..." : "رفع"}</span>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        {!value && <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-sm text-[#1E90FF] hover:underline">{uploading ? "جاري الرفع..." : "اختر صورة"}</button>}
      </div>
    </div>
  );
};

// ==================== BOOKINGS TAB ====================
const BookingsTab = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/public/bookings", token);
      setBookings(res.data);
    } catch (err) { console.error(err); toast.error("خطأ في تحميل الحجوزات"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/public/bookings/${id}`, { status }, token);
      toast.success(status === "confirmed" ? "تم تأكيد الحجز" : "تم التحديث");
      fetchBookings();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("حذف هذا الحجز؟")) return;
    try {
      await api.delete(`/public/bookings/${id}`, token);
      toast.success("تم الحذف");
      fetchBookings();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);
  const statusColors = { pending: "bg-yellow-100 text-yellow-700", confirmed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };
  const statusLabels = { pending: "بانتظار", confirmed: "مؤكد", cancelled: "ملغي" };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          { label: "الكل", count: bookings.length, color: "bg-blue-50 text-blue-700" },
          { label: "بانتظار", count: bookings.filter(b => b.status === "pending").length, color: "bg-yellow-50 text-yellow-700" },
          { label: "مؤكدة", count: bookings.filter(b => b.status === "confirmed").length, color: "bg-green-50 text-green-700" },
          { label: "ملغية", count: bookings.filter(b => b.status === "cancelled").length, color: "bg-red-50 text-red-700" }
        ].map((s, i) => (
          <div key={i} className={`p-3 rounded-xl ${s.color}`}>
            <p className="text-xl font-bold">{s.count}</p>
            <p className="text-xs">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        {[{ key: "all", label: "الكل" }, { key: "pending", label: "بانتظار" }, { key: "confirmed", label: "مؤكدة" }, { key: "cancelled", label: "ملغية" }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === f.key ? "bg-[#1E90FF] text-white" : "bg-gray-100 text-gray-600"}`}>{f.label}</button>
        ))}
        <button onClick={fetchBookings} className="px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-600 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> تحديث</button>
      </div>
      {loading ? <p className="text-center py-8 text-gray-400">جاري التحميل...</p> : filtered.length === 0 ? <p className="text-center py-8 text-gray-400">لا توجد حجوزات</p> : (
        <div className="space-y-2">
          {filtered.map(b => (
            <div key={b.id} className="bg-white border rounded-xl p-3 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-sm text-gray-800">{b.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[b.status] || "bg-gray-100"}`}>{statusLabels[b.status] || b.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span>{b.phone}</span><span>{b.service}</span>{b.preferred_date && <span>{b.preferred_date}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {b.status === "pending" && <>
                    <button onClick={() => updateStatus(b.id, "confirmed")} className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"><CheckCircle className="w-4 h-4" /></button>
                    <button onClick={() => updateStatus(b.id, "cancelled")} className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"><XCircle className="w-4 h-4" /></button>
                  </>}
                  <button onClick={() => deleteBooking(b.id)} className="p-1.5 bg-gray-50 text-gray-400 rounded-lg hover:bg-red-50 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== GENERIC CONTENT EDITOR ====================
const ContentEditor = ({ section, token, fields, title }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/website/content/${section}`, token);
        if (res.data?.data) setData(res.data.data);
      } catch (err) { console.error("Load error:", err); } finally { setLoading(false); }
    })();
  }, [section, token]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/website/content/${section}`, data, token);
      toast.success("تم الحفظ بنجاح");
    } catch (err) { console.error(err); toast.error("خطأ في الحفظ"); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-center py-8 text-gray-400">جاري التحميل...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">{title}</h3>
        <button onClick={save} disabled={saving} className="px-4 py-2 bg-[#1E90FF] text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#1a7fe0] disabled:opacity-60" data-testid={`save-${section}`}>
          <Save className="w-4 h-4" /> {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
        </button>
      </div>
      <div className="space-y-4">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-600 mb-1">{field.label}</label>
            {field.type === "textarea" ? (
              <textarea rows={3} value={data[field.key] || ""} onChange={e => setData({ ...data, [field.key]: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:border-[#1E90FF] focus:outline-none" placeholder={field.placeholder} />
            ) : field.type === "image" ? (
              <ImageUpload value={data[field.key] || ""} onChange={v => setData({ ...data, [field.key]: v })} token={token} />
            ) : (
              <input type={field.type || "text"} value={data[field.key] || ""} onChange={e => setData({ ...data, [field.key]: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:border-[#1E90FF] focus:outline-none" placeholder={field.placeholder} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== LIST EDITOR (Services, Testimonials, FAQ) ====================
const ListEditor = ({ section, token, itemFields, title, emptyItem }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/website/content/${section}`, token);
        if (res.data?.data?.items) setItems(res.data.data.items);
      } catch (err) { console.error("Load error:", err); } finally { setLoading(false); }
    })();
  }, [section, token]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/website/content/${section}`, { items }, token);
      toast.success("تم الحفظ بنجاح");
    } catch (err) { console.error(err); toast.error("خطأ في الحفظ"); }
    finally { setSaving(false); }
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem, id: Date.now().toString() }]);
    setEditingIdx(items.length);
  };

  const removeItem = (idx) => {
    if (!window.confirm("حذف هذا العنصر؟")) return;
    setItems(items.filter((_, i) => i !== idx));
    setEditingIdx(null);
  };

  const updateItem = (idx, key, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [key]: val };
    setItems(updated);
  };

  if (loading) return <p className="text-center py-8 text-gray-400">جاري التحميل...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">{title} ({items.length})</h3>
        <div className="flex gap-2">
          <button onClick={addItem} className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-green-600">
            <Plus className="w-4 h-4" /> إضافة
          </button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-[#1E90FF] text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#1a7fe0] disabled:opacity-60" data-testid={`save-${section}`}>
            <Save className="w-4 h-4" /> {saving ? "حفظ..." : "حفظ"}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-center py-8 text-gray-400">لا توجد عناصر. اضغط "إضافة" لإضافة عنصر جديد.</p>}
        {items.map((item, idx) => (
          <div key={item.id || idx} className="bg-white border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50" onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}>
              <span className="font-medium text-sm text-gray-800">{item[itemFields[0].key] || `عنصر ${idx + 1}`}</span>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); removeItem(idx); }} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                {editingIdx === idx ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>
            {editingIdx === idx && (
              <div className="p-3 pt-0 space-y-3 border-t bg-gray-50">
                {itemFields.map(field => (
                  <div key={field.key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{field.label}</label>
                    {field.type === "textarea" ? (
                      <textarea rows={2} value={item[field.key] || ""} onChange={e => updateItem(idx, field.key, e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm focus:border-[#1E90FF] focus:outline-none" />
                    ) : field.type === "image" ? (
                      <ImageUpload value={item[field.key] || ""} onChange={v => updateItem(idx, field.key, v)} token={token} />
                    ) : field.type === "number" ? (
                      <input type="number" value={item[field.key] || ""} onChange={e => updateItem(idx, field.key, e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm focus:border-[#1E90FF] focus:outline-none" />
                    ) : (
                      <input type="text" value={item[field.key] || ""} onChange={e => updateItem(idx, field.key, e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm focus:border-[#1E90FF] focus:outline-none" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== GALLERY TAB ====================
const GalleryTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/website/gallery", token);
      setItems(res.data);
    } catch (err) { console.error("Load error:", err); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const addItem = async () => {
    try {
      const res = await api.post("/website/gallery", { title_ar: "حالة جديدة", title_en: "New Case", order: items.length }, token);
      if (res.data.success) { fetchGallery(); toast.success("تم الإضافة"); }
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("حذف هذا العنصر؟")) return;
    try {
      await api.delete(`/website/gallery/${id}`, token);
      toast.success("تم الحذف");
      fetchGallery();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const updateImage = async (id, field, url) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    const updated = { ...item, [field]: url };
    try {
      await api.post("/website/gallery", updated, token);
      fetchGallery();
    } catch (err) { console.error("Gallery reorder error:", err); }
  };

  if (loading) return <p className="text-center py-8 text-gray-400">جاري التحميل...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">صور قبل وبعد ({items.length})</h3>
        <button onClick={addItem} className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-green-600">
          <Plus className="w-4 h-4" /> إضافة حالة
        </button>
      </div>
      {items.length === 0 ? <p className="text-center py-8 text-gray-400">لا توجد صور. اضغط "إضافة حالة" لإضافة صورة قبل وبعد.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white border rounded-xl p-3 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{item.title_ar || "حالة"}</span>
                <button onClick={() => deleteItem(item.id)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ImageUpload value={item.before_image} onChange={v => updateImage(item.id, "before_image", v)} label="قبل" token={token} />
                <ImageUpload value={item.after_image} onChange={v => updateImage(item.id, "after_image", v)} label="بعد" token={token} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== PHOTOS TAB ====================
const PHOTO_CATEGORIES = [
  { value: "general", label: "عام" },
  { value: "clinic", label: "المركز" },
  { value: "results", label: "نتائج العمليات" },
  { value: "team", label: "الفريق الطبي" },
  { value: "equipment", label: "الأجهزة والتقنيات" },
];

const PhotosTab = ({ token }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/website/photos", token);
      setPhotos(res.data);
    } catch (err) { console.error("Load error:", err); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const uploadRes = await fetch(`${API_BASE}/api/website/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          await api.post("/website/photos", {
            image: uploadData.url,
            title_ar: file.name.split(".")[0],
            title_en: file.name.split(".")[0],
            category: filter === "all" ? "general" : filter,
          }, token);
        }
      } catch (err) { console.error(err); toast.error(`فشل رفع ${file.name}`); }
    }
    
    toast.success(`تم رفع ${files.length} صورة`);
    setUploading(false);
    fetchPhotos();
    if (fileRef.current) fileRef.current.value = "";
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("حذف هذه الصورة؟")) return;
    try {
      await api.delete(`/website/photos/${id}`, token);
      toast.success("تم الحذف");
      fetchPhotos();
    } catch (err) { console.error(err); toast.error("خطأ في الحذف"); }
  };

  const filtered = filter === "all" ? photos : photos.filter(p => p.category === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">معرض الصور ({photos.length})</h3>
        <div className="flex gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-green-600 disabled:opacity-60">
            <Upload className="w-4 h-4" /> {uploading ? "جاري الرفع..." : "رفع صور"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === "all" ? "bg-[#1E90FF] text-white" : "bg-gray-100 text-gray-600"}`}>الكل ({photos.length})</button>
        {PHOTO_CATEGORIES.map(cat => {
          const count = photos.filter(p => p.category === cat.value).length;
          return (
            <button key={cat.value} onClick={() => setFilter(cat.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === cat.value ? "bg-[#1E90FF] text-white" : "bg-gray-100 text-gray-600"}`}>
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Photos Grid */}
      {loading ? <p className="text-center py-8 text-gray-400">جاري التحميل...</p> : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">لا توجد صور. اضغط "رفع صور" لإضافة صور.</p>
          <p className="text-gray-300 text-xs mt-1">يمكنك رفع عدة صور دفعة واحدة</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map(photo => (
            <div key={photo.id} className="group relative bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-square">
                <img src={imgUrl(photo.image)} alt={photo.title_ar} className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <button onClick={() => deletePhoto(photo.id)} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="p-2">
                <p className="text-xs text-gray-600 truncate">{photo.title_ar}</p>
                <span className="text-xs text-gray-400">{PHOTO_CATEGORIES.find(c => c.value === photo.category)?.label || "عام"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function WebsiteManagePage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("bookings");

  const renderTab = () => {
    switch (activeTab) {
      case "bookings": return <BookingsTab token={token} />;
      case "hero": return (
        <ContentEditor section="hero" token={token} title="الصفحة الرئيسية" fields={[
          { key: "title_ar", label: "العنوان (عربي)", placeholder: "المركز الوطني التركي" },
          { key: "title_en", label: "Title (English)", placeholder: "Vatan Hair" },
          { key: "subtitle_ar", label: "العنوان الفرعي (عربي)", placeholder: "لزراعة الشعر والتجميل" },
          { key: "subtitle_en", label: "Subtitle (English)", placeholder: "Hair Transplant & Cosmetics Center" },
          { key: "desc_ar", label: "الوصف (عربي)", type: "textarea", placeholder: "نقدم أحدث تقنيات..." },
          { key: "desc_en", label: "Description (English)", type: "textarea", placeholder: "We offer the latest..." },
          { key: "hero_image", label: "صورة الخلفية", type: "image" },
          { key: "about_ar", label: "عن المركز (عربي)", type: "textarea" },
          { key: "about_en", label: "About (English)", type: "textarea" },
          { key: "about_image", label: "صورة القسم", type: "image" },
        ]} />
      );
      case "services": return (
        <ListEditor section="services" token={token} title="الخدمات" emptyItem={{ title_ar: "", title_en: "", desc_ar: "", desc_en: "" }} itemFields={[
          { key: "title_ar", label: "اسم الخدمة (عربي)" },
          { key: "title_en", label: "Service Name (English)" },
          { key: "desc_ar", label: "الوصف (عربي)", type: "textarea" },
          { key: "desc_en", label: "Description (English)", type: "textarea" },
          { key: "image", label: "صورة الخدمة", type: "image" },
        ]} />
      );
      case "gallery": return <GalleryTab token={token} />;
      case "photos": return <PhotosTab token={token} />;
      case "testimonials": return (
        <ListEditor section="testimonials" token={token} title="آراء العملاء" emptyItem={{ name_ar: "", name_en: "", text_ar: "", text_en: "", rating: "5" }} itemFields={[
          { key: "name_ar", label: "اسم العميل (عربي)" },
          { key: "name_en", label: "Client Name (English)" },
          { key: "text_ar", label: "الرأي (عربي)", type: "textarea" },
          { key: "text_en", label: "Testimonial (English)", type: "textarea" },
          { key: "rating", label: "التقييم (1-5)", type: "number" },
        ]} />
      );
      case "faq": return (
        <ListEditor section="faq" token={token} title="الأسئلة الشائعة" emptyItem={{ q_ar: "", q_en: "", a_ar: "", a_en: "" }} itemFields={[
          { key: "q_ar", label: "السؤال (عربي)" },
          { key: "q_en", label: "Question (English)" },
          { key: "a_ar", label: "الجواب (عربي)", type: "textarea" },
          { key: "a_en", label: "Answer (English)", type: "textarea" },
        ]} />
      );
      case "stats": return (
        <ContentEditor section="stats" token={token} title="العدادات والإنجازات" fields={[
          { key: "operations", label: "عدد العمليات الناجحة", type: "number", placeholder: "5000" },
          { key: "years", label: "سنوات الخبرة", type: "number", placeholder: "9" },
          { key: "patients", label: "عدد المرضى", type: "number", placeholder: "4500" },
          { key: "rating", label: "تقييم المرضى", placeholder: "4.9" },
        ]} />
      );
      case "contact": return (
        <ContentEditor section="contact" token={token} title="معلومات التواصل" fields={[
          { key: "address_ar", label: "العنوان (عربي)", type: "textarea" },
          { key: "address_en", label: "Address (English)", type: "textarea" },
          { key: "phone1", label: "رقم الهاتف 1", placeholder: "07723220220" },
          { key: "phone2", label: "رقم الهاتف 2", placeholder: "078220220220" },
          { key: "whatsapp", label: "رقم واتساب", placeholder: "96407723220220" },
          { key: "hours_ar", label: "ساعات العمل (عربي)", placeholder: "من 9 صباحاً إلى 8 مساءً" },
          { key: "hours_en", label: "Working Hours (English)", placeholder: "9:00 AM to 8:00 PM" },
          { key: "instagram", label: "رابط انستغرام", placeholder: "https://www.instagram.com/vatanhair19/" },
          { key: "facebook", label: "رابط فيسبوك", placeholder: "https://www.facebook.com/VatanHair19/" },
          { key: "maps_embed", label: "رابط خريطة Google Maps (embed)", type: "textarea", placeholder: "https://www.google.com/maps/embed?..." },
        ]} />
      );
      case "offers": return <OffersReviewsPage />;
      case "instagram": return <InstagramManagePage />;
      case "leads": return <LeadsPage />;
      case "ai-dashboard": return <AIDashboardPage />;
      default: return null;
    }
  };

  return (
    <div className="page-content" data-testid="website-manage-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Globe className="w-7 h-7 text-[#1E90FF]" />
            إدارة الموقع الإلكتروني
          </h1>
          <p className="text-sm text-gray-500 mt-1">تعديل محتوى الموقع والحجوزات</p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-[#2F4F4F] text-white rounded-lg text-sm flex items-center gap-2 hover:bg-[#1E90FF] transition-colors" data-testid="view-website-btn">
          <Eye className="w-4 h-4" /> عرض الموقع
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-2 scrollbar-hide" data-testid="cms-tabs">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? "bg-[#1E90FF] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border p-4 sm:p-6">
        {renderTab()}
      </div>
    </div>
  );
}
